import type { UseQueryOptions } from '@tanstack/react-query';
import { IProducts } from 'gcf-components/interfaces';
import { cloneDeep, merge } from 'lodash';
import { lazy, memo, useContext, useEffect, useMemo, useState } from 'react';
import { GCFProductsContext } from 'src/components/context';
import { ProgressBox } from 'src/components/progress';
import {
  useGetDatasourceProducts,
  useGetProjectConfig,
  useGetThemes,
} from 'src/hooks';
import { type IThemeRes, ProjectConfigSchema } from 'src/interfaces';
import $schema from 'src/utils/schema-helper';

interface GCFConfigProviderProps {
  children?: React.ReactNode;
  overrideQuerySetting?: UseQueryOptions<any>;
  screenSectionId?: number | undefined;
  productFilters?: string[];
  productVariationFilters?: string[];
  skipProductsApiOnEmptyFilters?: boolean;
  overrideActiveTrue?: boolean;
  /** Optional DY templates to expose to gcf-components ConfigProvider (used by variation template pages). */
  templates?: any;
  /** Optional mapping between template slots and products/variations (used by variation template pages). */
  templateVariations?: any;
}

const ConfigProvider = lazy((): any =>
  import('gcf-components/Components').then((module) => ({
    default: module.default.ConfigProvider,
  })),
);

export const formatThemes = (themes?: IThemeRes[]) => {
  return themes?.reduce((acc, theme) => {
    acc[theme.name] = theme.config;

    return acc;
  }, {} as any);
};

export const setProductsActiveTrue = (products: IProducts) => {
  const cloned = cloneDeep(products);

  Object.values(cloned).forEach((product) => {
    product.active = true;

    Object.values(product.variations).forEach((variation) => {
      variation.active = true;
    });
  });

  return cloned;
};

export const GCFConfigProvider = memo((props: GCFConfigProviderProps) => {
  if (props.screenSectionId) {
    return <GCFConfigProviderEventBased {...props} />;
  } else {
    return <GCFConfigProviderContextBased {...props} />;
  }
});

const GCFConfigProviderContextBased = memo((props: GCFConfigProviderProps) => {
  const gcfProducts = useContext(GCFProductsContext);

  // Check if we should skip the products API call when filters are empty
  const shouldSkipProductsApi =
    props.skipProductsApiOnEmptyFilters &&
    (!props.productFilters || props.productFilters.length === 0) &&
    (!props.productVariationFilters ||
      props.productVariationFilters.length === 0);

  const { data: products, isLoading: productsLoading } =
    useGetDatasourceProducts(
      shouldSkipProductsApi ? [] : props.productFilters || [],
      shouldSkipProductsApi ? [] : props.productVariationFilters || [],
      shouldSkipProductsApi ? { enabled: false } : props.overrideQuerySetting,
    );

  const { data: projectConfig, isLoading: projectConfigLoading } =
    useGetProjectConfig(undefined, props.overrideQuerySetting);

  // TODO: Add schema validation message/alert component and move this there
  const isValidProjectConfig = $schema.validate(
    projectConfig,
    ProjectConfigSchema,
  );

  const { data: themes, isLoading: themesLoading } = useGetThemes(
    undefined,
    props.overrideQuerySetting,
  );

  const memoizedProducts = useMemo(() => {
    const mergedProducts = merge(
      {},
      shouldSkipProductsApi ? {} : products,
      gcfProducts?.value,
    );

    return props.overrideActiveTrue
      ? setProductsActiveTrue(mergedProducts)
      : mergedProducts;
  }, [
    products,
    gcfProducts?.value,
    shouldSkipProductsApi,
    props.overrideActiveTrue,
  ]);

  const memoizedThemes = useMemo(() => formatThemes(themes), [themes]);

  const memoizedProjectConfig = useMemo(
    () => (isValidProjectConfig ? projectConfig : undefined),
    [isValidProjectConfig, projectConfig],
  );

  if (
    (shouldSkipProductsApi ? false : productsLoading) ||
    projectConfigLoading ||
    themesLoading
  )
    return <ProgressBox loading />;

  return (
    <ConfigProvider
      isPreview
      products={memoizedProducts}
      themes={memoizedThemes}
      projectConfig={memoizedProjectConfig}
      templates={props.templates}
      templateVariations={props.templateVariations}>
      {props.children ?? null}
    </ConfigProvider>
  );
});

const GCFConfigProviderEventBased = memo((props: GCFConfigProviderProps) => {
  const [gcfProducts, setGcfProducts] = useState(window.__GCF_PRODUCTS__ ?? {});

  const eventName = `onChangeGCFProductStatus-${props.screenSectionId}`;
  const onChangeGCFProductStatus = (gcfProductEvent: CustomEvent) => {
    setGcfProducts(gcfProductEvent.detail);
    window.__GCF_PRODUCTS__ = gcfProducts;
  };

  useEffect(() => {
    if (!props.screenSectionId) return;
    window.addEventListener(
      eventName,
      onChangeGCFProductStatus as EventListener,
    );

    return () =>
      window.removeEventListener(
        eventName,
        onChangeGCFProductStatus as EventListener,
      );
  });

  const shouldSkipProductsApi =
    props.skipProductsApiOnEmptyFilters &&
    (!props.productFilters || props.productFilters.length === 0) &&
    (!props.productVariationFilters ||
      props.productVariationFilters.length === 0);

  const { data: products, isLoading: productsLoading } =
    useGetDatasourceProducts(
      shouldSkipProductsApi ? [] : props.productFilters || [],
      shouldSkipProductsApi ? [] : props.productVariationFilters || [],
      shouldSkipProductsApi ? { enabled: false } : props.overrideQuerySetting,
    );

  const { data: projectConfig, isLoading: projectConfigLoading } =
    useGetProjectConfig(undefined, props.overrideQuerySetting);

  // TODO: Add schema validation message/alert component and move this there
  const isValidProjectConfig = $schema.validate(
    projectConfig,
    ProjectConfigSchema,
  );

  const { data: themes, isLoading: themesLoading } = useGetThemes(
    undefined,
    props.overrideQuerySetting,
  );

  const memoizedProducts = useMemo(
    () => merge({}, shouldSkipProductsApi ? {} : products, gcfProducts),
    [products, gcfProducts, shouldSkipProductsApi],
  );

  const memoizedThemes = useMemo(() => formatThemes(themes), [themes]);

  const memoizedProjectConfig = useMemo(
    () => (isValidProjectConfig ? projectConfig : undefined),
    [isValidProjectConfig, projectConfig],
  );

  if (
    (shouldSkipProductsApi ? false : productsLoading) ||
    projectConfigLoading ||
    themesLoading
  )
    return <ProgressBox loading />;

  return (
    <ConfigProvider
      isPreview
      products={memoizedProducts}
      themes={memoizedThemes}
      projectConfig={memoizedProjectConfig}
      templates={props.templates}
      templateVariations={props.templateVariations}>
      {props.children ?? null}
    </ConfigProvider>
  );
});
